import getBestBidColumn from "./getBestBidColumn";
import getNumberFromString from "./getNumber";


/*  
    This function will take set of unique bids
    as argument and sorts the bids according
    to the sorting order and filters the top
    bids
*/
function sortAndFilterTopBids(
    bids: Set<number>,
    sortInAscendingOrder: boolean,
    numberOfTopBidsToFilter: number
): Array<number> {
    const filteredTopBids = Array.from(bids).sort((a, b) => {
        if (sortInAscendingOrder) {
            return a - b;
        }else {
            return b - a;
        }
    }).slice(0, numberOfTopBidsToFilter);
    return filteredTopBids
}

function checkSortOrderIsAscending(eventInfo) {
    let sortInAscendingOrder = true;
    const isSourcingSell = eventInfo.event_type === 'sourcing' && eventInfo.sourcing_type === 'sell';
    const isForwardAuction = eventInfo.event_type === 'auction' && eventInfo.auction_type === 'b';
    const isDutchAuctionWithDecreasingPrice = eventInfo.event_type === 'auction' && eventInfo.auction_type === 'c' && eventInfo.price_movement === 'decreases';
    if (
        isSourcingSell
        || isForwardAuction 
        || isDutchAuctionWithDecreasingPrice
    ) {
        sortInAscendingOrder = false;
    }
    return sortInAscendingOrder
}

function prepareLotLevelTopBids(
    supplierResponses,
    eventInfo,
    numberOfTopBidsToFilter,
    formatValue,
    bestBidColumnId,
    columnInfo
) {
    const bestBidColumnInfo = columnInfo[bestBidColumnId]
    const lotLevelUniqueBids = new Set();
    for (let supplier in supplierResponses) {
        const supplierResponse = supplierResponses[supplier]
        const lotTotalValue = getNumberFromString(supplierResponse.totals_row[bestBidColumnId])
        if (lotTotalValue > 0) {
            lotLevelUniqueBids.add(lotTotalValue);
        }
    }
    const sortInAscendingOrder = checkSortOrderIsAscending(eventInfo);
    const lotLevelTopBids = sortAndFilterTopBids(
        lotLevelUniqueBids,
        sortInAscendingOrder,
        numberOfTopBidsToFilter
    )
    const lotLevelFormattedTopBids = lotLevelTopBids.map(
        bidValue => formatValue(
            bidValue,
            bestBidColumnInfo.decimal_spaces
        )
    );
    return lotLevelFormattedTopBids
}

function prepareLineItemLevelTopBids(
    supplierResponses,
    eventInfo,
    numberOfTopBidsToFilter,
    formatValue,
    bestBidColumnId,
    columnInfo
) {
    const lineItemLevelUniqueBids = new Map()
    const bestBidColumnInfo = columnInfo[bestBidColumnId]
    // preparing the map which contains only the unique bids for each line item
    for (let supplier in supplierResponses) {
        const supplierResponse = supplierResponses[supplier];
        const rowData = supplierResponse.row_data
        for( let row in rowData) {
            const lineItemBidValue = getNumberFromString(rowData[row][bestBidColumnId])
            if (lineItemBidValue <= 0) {
                continue
            }
            if ( lineItemLevelUniqueBids.has(rowData[row].urid)) {
                const currentRowBids = lineItemLevelUniqueBids.get(rowData[row].urid);
                currentRowBids.add(lineItemBidValue)
            }else {
                lineItemLevelUniqueBids.set(rowData[row].urid, new Set([lineItemBidValue]))
            }
        }
    }

    const sortInAscendingOrder = checkSortOrderIsAscending(eventInfo);
    const lineItemLevelTopBids = new Map()
    // sorting and filtering the top bids of each line item
    for (const lineItemId of lineItemLevelUniqueBids.keys()) {

        let currentLineItemBids = lineItemLevelUniqueBids.get(lineItemId);
        const topBidsOfCurrentLineItem = sortAndFilterTopBids(
            currentLineItemBids,
            sortInAscendingOrder,
            numberOfTopBidsToFilter
        );

        // formatting the top bids of a line item
        const lineItemLevelFormattedTopBids = topBidsOfCurrentLineItem.map(
            bidValue => formatValue(
                bidValue,
                bestBidColumnInfo.decimal_spaces
            )
        );
        lineItemLevelTopBids.set(lineItemId, lineItemLevelFormattedTopBids)
    }
    return lineItemLevelTopBids;
}

function prepareTopBids(response, eventInfo, numberOfTopBidsToFilter, formatValue) {
    const bestBidColumnId = getBestBidColumn(response)
    const topBids = new Map();
    const lotLevelTopBids = prepareLotLevelTopBids(
        response.supplier_responses,
        eventInfo,
        numberOfTopBidsToFilter,
        formatValue,
        bestBidColumnId,
        response.column_info
    )
    topBids.set('lotLevelTopBids', lotLevelTopBids)
    if (!response.compute_transformed_cost
        || response.compute_transformed_cost == "no"
        || (
            response.compute_transformed_cost == "yes"
            && response.isTransformationAppliedAtLineItemLevel == true
        )
    ) {
        const lineItemLevelTopBids = prepareLineItemLevelTopBids(
            response.supplier_responses,
            eventInfo,
            numberOfTopBidsToFilter,
            formatValue,
            bestBidColumnId,
            response.column_info
        )
        topBids.set('lineItemLevelTopBids', lineItemLevelTopBids)
    }
    else {
        topBids.set('lineItemLevelTopBids', new Map())
    }
    return topBids
}

function updateBestBidsOfLineItems(response) {
    const lineItemLevelTopBids = response.bestBids.get('lineItemLevelTopBids')
    for (let row of response.items['row_data']){
        if (lineItemLevelTopBids.has(row.urid)) {
            row['best_bid'] = lineItemLevelTopBids.get(row.urid)[0]
        } else{
            row['best_bid'] = "-"
        }
    }
}
function calculatePercentageDifference(bestBid, bidValue, formatValue) {
    const absoluteDifference = Math.abs(bestBid - bidValue);
    const percentageDifference = (absoluteDifference / bestBid) * 100;
    return formatValue(percentageDifference, 2)
}

function updatePercentageDifferenceFromBestBid(response, formatValue) {
    const lineItemLevelTopBids = response.bestBids.get('lineItemLevelTopBids')
    const lotLevelTopBids = response.bestBids.get('lotLevelTopBids')
    const bestBidColumnId = getBestBidColumn(response)
    const bestBidOfCurrentLot = getNumberFromString(lotLevelTopBids[0])
    for (let sup in response.supplier_responses){
        if(
            response.supplier_responses[sup]['totals_row']
            && response.supplier_responses[sup]['totals_row'][bestBidColumnId]
        ){
            const bidValueOfCurrentSupplier = getNumberFromString(response.supplier_responses[sup]['totals_row'][bestBidColumnId])
            const percentageDifference = calculatePercentageDifference(
                bestBidOfCurrentLot,
                bidValueOfCurrentSupplier,
                formatValue
            )
            response.supplier_responses[sup]['totals_row']['percentage_away_from_best_bid'] = percentageDifference
        }
        if (
            response.supplier_responses[sup]['row_data']
            && Object.values(response.supplier_responses[sup]['row_data']).length > 0
        ){
            for (let lineItemId in response.supplier_responses[sup]['row_data']) {
                let lineItem = response.supplier_responses[sup]['row_data'][lineItemId]
                if (
                    lineItemLevelTopBids.has(lineItem.urid)
                    && lineItem[bestBidColumnId]
                ) {
                    const bestBidOfCurrentLineItem = getNumberFromString(lineItemLevelTopBids.get(lineItem.urid)[0])
                    const bidValueOfCurrentLineItem = getNumberFromString(lineItem[bestBidColumnId])
                    const percentageDifference = calculatePercentageDifference(
                        bestBidOfCurrentLineItem,
                        bidValueOfCurrentLineItem,
                        formatValue
                    )
                    lineItem['percentage_away_from_best_bid'] = percentageDifference
                } 
            }
        }
    }
}

function updateLotLevelPercentageDifferenceFromBestBid(
    supplierResponses,
    bestBidOfLot,
    bestBidColumnId,
    formatValue
){
    const bestBidOfCurrentLot = getNumberFromString(bestBidOfLot)
    for (let supplier in supplierResponses){
        const bidValueOfCurrentSupplier = getNumberFromString(supplierResponses[supplier]['totals_row'][bestBidColumnId])
        const percentageDifference = calculatePercentageDifference(
            bestBidOfCurrentLot,
            bidValueOfCurrentSupplier,
            formatValue
        )
        supplierResponses[supplier]['totals_row']['percentage_away_from_best_bid'] = percentageDifference
    }
}

export {
    prepareTopBids,
    prepareLotLevelTopBids,
    updateBestBidsOfLineItems,
    updatePercentageDifferenceFromBestBid,
    updateLotLevelPercentageDifferenceFromBestBid
};